import { supabase } from '@/integrations/supabase/client';
import { adminSupabase } from '@/lib/admin';
import type { GlobalSideBet, GlobalSideBetPick } from '@/types/side-bets';
import type { FeedbackInput, FeedbackResponse } from '@/types/feedback';

// ── Groups ──────────────────────────────────────────
export async function fetchUserGroups(userId: string) {
  const { data: memberships } = await supabase
    .from('group_members')
    .select('group_id, role')
    .eq('user_id', userId);

  if (!memberships?.length) return [];

  const groupIds = memberships.map(m => m.group_id).filter(Boolean) as string[];
  const { data: groups } = await supabase
    .from('groups')
    .select('*')
    .in('id', groupIds);

  if (!groups) return [];

  // Get member counts
  const { data: counts } = await supabase
    .from('group_members')
    .select('group_id')
    .in('group_id', groupIds);

  const countMap: Record<string, number> = {};
  counts?.forEach(c => {
    if (c.group_id) countMap[c.group_id] = (countMap[c.group_id] || 0) + 1;
  });

  // Get commissioner names
  const commissionerMemberships = memberships.filter(m => m.role === 'commissioner');
  const commMap: Record<string, string> = {};
  if (commissionerMemberships.length) {
    // Current user is commissioner of some groups
    // For other groups, we need to find the commissioner
  }

  const { data: commissioners } = await supabase
    .from('group_members')
    .select('group_id, display_name')
    .in('group_id', groupIds)
    .eq('role', 'commissioner');

  commissioners?.forEach(c => {
    if (c.group_id) commMap[c.group_id] = c.display_name;
  });

  return groups.map(g => ({
    id: g.id,
    name: g.name,
    inviteCode: g.invite_code,
    members: countMap[g.id] || 0,
    commissioner: commMap[g.id] || '',
  }));
}

export async function createGroup(name: string, userId: string, displayName: string, avatarId?: number | null) {
  const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();
  const { data: group, error: groupError } = await supabase
    .from('groups')
    .insert({ name, invite_code: inviteCode, created_by: userId })
    .select()
    .single();

  if (groupError || !group) throw groupError || new Error('Failed to create group');

  const { error: memberError } = await supabase
    .from('group_members')
    .insert({ group_id: group.id, user_id: userId, role: 'commissioner', display_name: displayName, avatar_id: avatarId ?? null });

  if (memberError) throw memberError;

  return group;
}

export async function lookupGroupByInviteCode(code: string) {
  // Uses a SECURITY DEFINER RPC that returns only {id, name, invite_code} for the
  // supplied code. Direct SELECT on groups is no longer open to non-members
  // (M-1 fix) — the wide "Anyone can look up group by invite code" policy was
  // dropped because USING(true) leaked telegram_chat_id / payment_link.
  const { data } = await supabase.rpc('lookup_group_by_invite_code', { p_code: code });
  return data && data.length ? data[0] : null;
}

export async function joinGroup(groupId: string, userId: string, displayName: string, avatarId?: number | null) {
  // Check if already a member
  const { data: existing } = await supabase
    .from('group_members')
    .select('id')
    .eq('group_id', groupId)
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) return { alreadyMember: true };

  const { error } = await supabase
    .from('group_members')
    .insert({ group_id: groupId, user_id: userId, role: 'member', display_name: displayName, avatar_id: avatarId ?? null });

  if (error) throw error;
  return { alreadyMember: false };
}

// ── Series ──────────────────────────────────────────
export async function fetchSeries() {
  const { data } = await supabase
    .from('series')
    .select('*')
    .order('created_at', { ascending: true });
  return data || [];
}

// ── Odds ────────────────────────────────────────────
export async function fetchSeriesOdds(seriesId: string) {
  const { data } = await supabase
    .from('series_odds')
    .select('*')
    .eq('series_id', seriesId);
  return data || [];
}

// ── Bets ────────────────────────────────────────────
export async function fetchUserBets(userId: string, groupId: string) {
  const { data, error } = await supabase
    .from('bets')
    .select('*')
    .eq('user_id', userId)
    .eq('group_id', groupId);
  if (error) {
    console.warn('fetchUserBets failed:', error.message);
    return [];
  }
  return data || [];
}

export async function fetchGroupBets(groupId: string) {
  const { data, error } = await supabase
    .from('bets')
    .select('*')
    .eq('group_id', groupId);
  if (error) {
    console.warn('fetchGroupBets failed:', error.message);
    return [];
  }
  return data || [];
}

export async function placeBet(params: {
  seriesId: string;
  groupId: string;
  userId: string;
  predictedWinner: string;
  predictedScore: string;
  multiplier: number;
  oddsAtBet: number | null;
  isGodMode?: boolean;
}) {
  // Check for existing bet
  const { data: existing } = await supabase
    .from('bets')
    .select('id')
    .eq('series_id', params.seriesId)
    .eq('group_id', params.groupId)
    .eq('user_id', params.userId)
    .maybeSingle();

  if (existing) {
    // God mode (commissioner editing) always allowed
    if (params.isGodMode) {
      await updateBet(existing.id, {
        predictedWinner: params.predictedWinner,
        predictedScore: params.predictedScore,
        multiplier: params.multiplier,
        oddsAtBet: params.oddsAtBet,
      });
      return;
    }

    // Regular users: allow edits until the series betting deadline
    const { data: series } = await supabase
      .from('series')
      .select('betting_deadline')
      .eq('id', params.seriesId)
      .maybeSingle();

    const isPastDeadline = series?.betting_deadline
      ? new Date(series.betting_deadline) < new Date()
      : false;

    if (isPastDeadline) {
      throw new Error('מועד ההימור לסדרה זו עבר');
    }

    await updateBet(existing.id, {
      predictedWinner: params.predictedWinner,
      predictedScore: params.predictedScore,
      multiplier: params.multiplier,
      oddsAtBet: params.oddsAtBet,
    });
    return;
  }

  const { error } = await supabase
    .from('bets')
    .insert({
      series_id: params.seriesId,
      group_id: params.groupId,
      user_id: params.userId,
      predicted_winner: params.predictedWinner,
      predicted_score: params.predictedScore,
      multiplier_at_bet: params.multiplier,
      odds_at_bet: params.oddsAtBet,
    });

  if (error) throw error;
}

// ── Commissioner ───────────────────────────────────

export interface MemberBetStatus {
  userId: string;
  displayName: string;
  role: string;
  betCount: number;
  totalActiveSeries: number;
  bettedSeriesIds: string[];
  commissionerEditedSeriesIds: string[];
  avatarId: number | null;
  totalSideBets: number;
  filledSideBets: number;
  pendingSeriesNames: string[];
  pendingSideBetNames: string[];
  totalOpen: number;
  totalFilled: number;
  hasPending: boolean;
}

export async function fetchGroupMembersWithBetStatus(groupId: string): Promise<MemberBetStatus[]> {
  // Fetch members, commissioner bet status, active series, open side bets, and side bet picks in parallel
  const [
    { data: members },
    { data: betStatus, error: rpcError },
    { data: activeSeries },
    { data: openSideBets },
    { data: allSideBetPicks },
  ] = await Promise.all([
    supabase
      .from('group_members')
      .select('user_id, display_name, role, avatar_id')
      .eq('group_id', groupId),
    supabase
      .rpc('get_commissioner_bet_status', { target_group_id: groupId }),
    supabase
      .from('series')
      .select('id, team_home, team_away')
      .in('status', ['upcoming', 'in_progress']),
    supabase
      .from('global_side_bets')
      .select('id, title_he')
      .eq('status', 'open'),
    supabase
      .from('global_side_bet_picks')
      .select('user_id, side_bet_id')
      .eq('group_id', groupId),
  ]);

  if (rpcError) {
    console.error('get_commissioner_bet_status RPC error:', rpcError);
  }

  // Build series lookup: id -> "TeamHome vs TeamAway"
  const seriesNameMap: Record<string, string> = {};
  (activeSeries || []).forEach((s: any) => {
    seriesNameMap[s.id] = `${s.team_home} vs ${s.team_away}`;
  });

  // Build open side bets lookup
  const openSideBetIds = new Set<string>();
  const sideBetNameMap: Record<string, string> = {};
  (openSideBets || []).forEach((sb: any) => {
    openSideBetIds.add(sb.id);
    sideBetNameMap[sb.id] = sb.title_he;
  });
  const totalSideBets = openSideBetIds.size;

  // Build user -> filled side bet IDs map (only for open side bets)
  const userSideBetPicksMap: Record<string, Set<string>> = {};
  (allSideBetPicks || []).forEach((p: any) => {
    if (openSideBetIds.has(p.side_bet_id)) {
      if (!userSideBetPicksMap[p.user_id]) userSideBetPicksMap[p.user_id] = new Set();
      userSideBetPicksMap[p.user_id].add(p.side_bet_id);
    }
  });

  // Collect unique active series IDs from the RPC result
  const activeSeriesIds = new Set<string>();
  const userBetsMap: Record<string, Set<string>> = {};
  const userCommEditedMap: Record<string, Set<string>> = {};

  (betStatus || []).forEach((row: any) => {
    activeSeriesIds.add(row.series_id);
    if (row.has_bet && row.user_id) {
      if (!userBetsMap[row.user_id]) userBetsMap[row.user_id] = new Set();
      userBetsMap[row.user_id].add(row.series_id);
      if (row.is_commissioner_edited) {
        if (!userCommEditedMap[row.user_id]) userCommEditedMap[row.user_id] = new Set();
        userCommEditedMap[row.user_id].add(row.series_id);
      }
    }
  });

  const totalActiveSeries = activeSeriesIds.size;

  const result: MemberBetStatus[] = (members || []).map(m => {
    const bettedIds = userBetsMap[m.user_id] || new Set<string>();
    const editedIds = userCommEditedMap[m.user_id] || new Set<string>();
    const filledSideBetIds = userSideBetPicksMap[m.user_id] || new Set<string>();

    // Compute pending series names (active series without a bet)
    const pendingSeriesNames: string[] = [];
    activeSeriesIds.forEach(sid => {
      if (!bettedIds.has(sid) && seriesNameMap[sid]) {
        pendingSeriesNames.push(seriesNameMap[sid]);
      }
    });

    // Compute pending side bet names (open side bets without a pick)
    const pendingSideBetNames: string[] = [];
    openSideBetIds.forEach(sbId => {
      if (!filledSideBetIds.has(sbId) && sideBetNameMap[sbId]) {
        pendingSideBetNames.push(sideBetNameMap[sbId]);
      }
    });

    const totalOpen = totalActiveSeries + totalSideBets;
    const totalFilled = bettedIds.size + filledSideBetIds.size;

    return {
      userId: m.user_id,
      displayName: m.display_name,
      role: m.role || 'member',
      betCount: bettedIds.size,
      totalActiveSeries,
      bettedSeriesIds: Array.from(bettedIds),
      commissionerEditedSeriesIds: Array.from(editedIds),
      avatarId: m.avatar_id ?? null,
      totalSideBets,
      filledSideBets: filledSideBetIds.size,
      pendingSeriesNames,
      pendingSideBetNames,
      totalOpen,
      totalFilled,
      hasPending: totalFilled < totalOpen,
    };
  });

  // Sort: incomplete members first (hasPending), then alphabetical
  result.sort((a, b) => {
    const aComplete = a.hasPending ? 0 : 1;
    const bComplete = b.hasPending ? 0 : 1;
    if (aComplete !== bComplete) return aComplete - bComplete;
    return a.displayName.localeCompare(b.displayName, 'he');
  });

  return result;
}

export async function isCommissioner(userId: string, groupId: string): Promise<boolean> {
  const { data } = await supabase
    .from('group_members')
    .select('role')
    .eq('user_id', userId)
    .eq('group_id', groupId)
    .maybeSingle();
  return data?.role === 'commissioner';
}

export async function kickMember(userId: string, groupId: string): Promise<void> {
  const { error } = await supabase
    .from('group_members')
    .delete()
    .eq('user_id', userId)
    .eq('group_id', groupId);
  if (error) throw error;
}

export async function sendCommissionerReminder(groupId: string): Promise<{ skipped?: boolean; reason?: string }> {
  const { data, error } = await supabase.functions.invoke('telegram-notify', {
    body: { event_type: 'commissioner_reminder', group_id: groupId },
  });
  if (error) throw error;
  return data ?? {};
}

export async function sendBetReminder(groupId: string, userIds: string[]): Promise<{ sent: number; skipped_no_key?: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-bet-reminder`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ group_id: groupId, user_ids: userIds }),
    }
  );
  if (!response.ok) throw new Error('Failed to send reminder');
  return response.json();
}

export async function updateBet(
  betId: string,
  updates: {
    predictedWinner: string;
    predictedScore: string;
    multiplier: number;
    oddsAtBet: number | null;
  }
): Promise<void> {
  const { error } = await supabase
    .from('bets')
    .update({
      predicted_winner: updates.predictedWinner,
      predicted_score: updates.predictedScore,
      multiplier_at_bet: updates.multiplier,
      odds_at_bet: updates.oddsAtBet,
    })
    .eq('id', betId);
  if (error) throw error;
}

export async function updateGroupSettings(
  groupId: string,
  updates: { name?: string; payment_link?: string; chat_link?: string; invite_restricted?: boolean }
): Promise<void> {
  // Separate settings-JSON fields from top-level column updates
  const { invite_restricted, ...columnUpdates } = updates;

  // If invite_restricted is provided, merge it into the existing settings JSON
  if (invite_restricted !== undefined) {
    const { data: group } = await supabase.from('groups').select('settings').eq('id', groupId).single();
    const settings = ((group?.settings as Record<string, unknown>) || {});
    settings.invite_restricted = invite_restricted;
    (columnUpdates as Record<string, unknown>).settings = settings;
  }

  const { error } = await supabase
    .from('groups')
    .update(columnUpdates)
    .eq('id', groupId);
  if (error) throw error;
}

// ── Leaderboard ─────────────────────────────────────
export async function fetchLeaderboard(groupId: string) {
  const { data } = await supabase
    .from('leaderboard')
    .select('*')
    .eq('group_id', groupId)
    .order('rank', { ascending: true });
  return data || [];
}

// ── Global Side Bets ─────────────────────────────────

export async function fetchGlobalSideBets(): Promise<GlobalSideBet[]> {
  const { data, error } = await supabase
    .from('global_side_bets')
    .select('*')
    .neq('status', 'draft')
    .order('round_number', { ascending: true })
    .order('template_id', { ascending: true });
  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    options: Array.isArray(row.options) ? row.options as string[] : [],
  })) as GlobalSideBet[];
}

export async function fetchGlobalSideBetPicks(betIds: string[], userId: string, groupId: string): Promise<Record<string, GlobalSideBetPick>> {
  if (!betIds.length) return {};
  const { data, error } = await supabase
    .from('global_side_bet_picks')
    .select('*')
    .in('side_bet_id', betIds)
    .eq('user_id', userId)
    .eq('group_id', groupId);
  if (error) throw error;
  const map: Record<string, GlobalSideBetPick> = {};
  (data || []).forEach((row: GlobalSideBetPick) => {
    map[row.side_bet_id] = row;
  });
  return map;
}

export async function pickGlobalSideBet(betId: string, userId: string, groupId: string, selectedOption: string, otherText?: string): Promise<void> {
  const { error } = await supabase
    .from('global_side_bet_picks')
    .upsert(
      { side_bet_id: betId, user_id: userId, group_id: groupId, selected_option: selectedOption, other_text: otherText ?? null },
      { onConflict: 'side_bet_id,user_id,group_id' }
    );
  if (error) throw error;
}

export interface ResolvedSideBetEntry {
  pick_id: string;
  side_bet_id: string;
  selected_option: string;
  other_text: string | null;
  points_earned: number;
  title_he: string;
  correct_answer: string | null;
  status: string;
  round_number: number;
  options: string[];
}

export async function fetchResolvedSideBetPicks(userId: string, groupId: string): Promise<ResolvedSideBetEntry[]> {
  const { data, error } = await supabase
    .from('global_side_bet_picks')
    .select('id, side_bet_id, selected_option, other_text, points_earned, global_side_bets!inner(title_he, correct_answer, status, round_number, options)')
    .eq('user_id', userId)
    .eq('group_id', groupId)
    .in('global_side_bets.status', ['resolved', 'pending_review'])
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    pick_id: row.id,
    side_bet_id: row.side_bet_id,
    selected_option: row.selected_option,
    other_text: row.other_text,
    points_earned: row.points_earned,
    title_he: row.global_side_bets.title_he,
    correct_answer: row.global_side_bets.correct_answer,
    status: row.global_side_bets.status,
    round_number: row.global_side_bets.round_number,
    options: Array.isArray(row.global_side_bets.options) ? row.global_side_bets.options : [],
  }));
}

export interface UserSideBetPick {
  pick_id: string;
  side_bet_id: string;
  selected_option: string;
  other_text: string | null;
  points_earned: number;
  created_at: string;
  title_he: string;
  correct_answer: string | null;
  status: GlobalSideBet['status'];
  round_number: number;
  options: string[];
  points_hit: number;
  points_other: number;
  has_other: boolean;
  deadline: string | null;
}

/**
 * Fetch all of the user's global side bet picks, regardless of status (open,
 * locked, pending_review, resolved). Joins in the parent side-bet row so
 * consumers have everything they need to render status, points, and options.
 */
export async function fetchAllUserSideBetPicks(userId: string, groupId: string): Promise<UserSideBetPick[]> {
  const { data, error } = await supabase
    .from('global_side_bet_picks')
    .select(
      'id, side_bet_id, selected_option, other_text, points_earned, created_at, ' +
      'global_side_bets!inner(title_he, correct_answer, status, round_number, options, points_hit, points_other, has_other, deadline)'
    )
    .eq('user_id', userId)
    .eq('group_id', groupId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    pick_id: row.id,
    side_bet_id: row.side_bet_id,
    selected_option: row.selected_option,
    other_text: row.other_text,
    points_earned: row.points_earned ?? 0,
    created_at: row.created_at,
    title_he: row.global_side_bets?.title_he ?? '',
    correct_answer: row.global_side_bets?.correct_answer ?? null,
    status: row.global_side_bets?.status ?? 'open',
    round_number: row.global_side_bets?.round_number ?? 0,
    options: Array.isArray(row.global_side_bets?.options) ? row.global_side_bets.options : [],
    points_hit: row.global_side_bets?.points_hit ?? 0,
    points_other: row.global_side_bets?.points_other ?? 0,
    has_other: Boolean(row.global_side_bets?.has_other),
    deadline: row.global_side_bets?.deadline ?? null,
  }));
}

// ── Odds Gatekeeper ───────────────────────────────────────────────────────

/**
 * Fetch the set of series_ids that already have at least one row in series_odds.
 * Used by SeriesCarousel to hide upcoming series that exist in DB but are not
 * yet ready for betting (commissioner hasn't pushed odds yet).
 */
export async function fetchSeriesIdsWithOdds(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('series_odds')
    .select('series_id');
  if (error) {
    console.warn('fetchSeriesIdsWithOdds failed:', error.message);
    return new Set();
  }
  return new Set(
    (data || [])
      .map((r: any) => r.series_id)
      .filter((id: any): id is string => typeof id === 'string' && id.length > 0),
  );
}

// ── Group Picks Reveal (social visibility on locked bets) ────────────────

export interface GroupSeriesPick {
  user_id: string;
  display_name: string;
  avatar_id: number | null;
  predicted_winner: string;
  predicted_score: string;
  points_earned: number | null;
  is_correct_winner: boolean | null;
  is_correct_score: boolean | null;
}

/**
 * Fetch every group member's pick for a series, along with their display name
 * and avatar. RLS only returns other members' bets when series.status is
 * 'in_progress' or 'completed' — call this only for locked/active series.
 */
export async function fetchSeriesPicksForGroup(
  groupId: string,
  seriesId: string,
): Promise<GroupSeriesPick[]> {
  const [betsRes, membersRes] = await Promise.all([
    supabase
      .from('bets')
      .select('user_id, predicted_winner, predicted_score, points_earned, is_correct_winner, is_correct_score')
      .eq('group_id', groupId)
      .eq('series_id', seriesId),
    supabase
      .from('group_members')
      .select('user_id, display_name, avatar_id')
      .eq('group_id', groupId),
  ]);
  if (betsRes.error) {
    console.warn('fetchSeriesPicksForGroup bets failed:', betsRes.error.message);
    return [];
  }
  if (membersRes.error) {
    console.warn('fetchSeriesPicksForGroup members failed:', membersRes.error.message);
    return [];
  }
  const memberMap: Record<string, { display_name: string; avatar_id: number | null }> = {};
  (membersRes.data || []).forEach((m: any) => {
    if (m.user_id) memberMap[m.user_id] = { display_name: m.display_name ?? '', avatar_id: m.avatar_id ?? null };
  });
  return (betsRes.data || [])
    .filter((b: any) => b.user_id && memberMap[b.user_id])
    .map((b: any) => ({
      user_id: b.user_id,
      display_name: memberMap[b.user_id].display_name,
      avatar_id: memberMap[b.user_id].avatar_id,
      predicted_winner: b.predicted_winner ?? '',
      predicted_score: b.predicted_score ?? '',
      points_earned: typeof b.points_earned === 'number' ? b.points_earned : null,
      is_correct_winner: typeof b.is_correct_winner === 'boolean' ? b.is_correct_winner : null,
      is_correct_score: typeof b.is_correct_score === 'boolean' ? b.is_correct_score : null,
    }));
}

export interface GroupSideBetPick {
  user_id: string;
  display_name: string;
  avatar_id: number | null;
  selected_option: string;
  other_text: string | null;
}

/**
 * Fetch every group member's pick for a single global side bet. RLS only
 * returns other members' picks when the parent bet status is locked,
 * pending_review, or resolved — call this only for closed bets.
 */
export async function fetchSideBetPicksForGroup(
  groupId: string,
  sideBetId: string,
): Promise<GroupSideBetPick[]> {
  const [picksRes, membersRes] = await Promise.all([
    supabase
      .from('global_side_bet_picks')
      .select('user_id, selected_option, other_text')
      .eq('group_id', groupId)
      .eq('side_bet_id', sideBetId),
    supabase
      .from('group_members')
      .select('user_id, display_name, avatar_id')
      .eq('group_id', groupId),
  ]);
  if (picksRes.error) {
    console.warn('fetchSideBetPicksForGroup picks failed:', picksRes.error.message);
    return [];
  }
  if (membersRes.error) {
    console.warn('fetchSideBetPicksForGroup members failed:', membersRes.error.message);
    return [];
  }
  const memberMap: Record<string, { display_name: string; avatar_id: number | null }> = {};
  (membersRes.data || []).forEach((m: any) => {
    if (m.user_id) memberMap[m.user_id] = { display_name: m.display_name ?? '', avatar_id: m.avatar_id ?? null };
  });
  return (picksRes.data || [])
    .filter((p: any) => p.user_id && memberMap[p.user_id])
    .map((p: any) => ({
      user_id: p.user_id,
      display_name: memberMap[p.user_id].display_name,
      avatar_id: memberMap[p.user_id].avatar_id,
      selected_option: p.selected_option ?? '',
      other_text: p.other_text ?? null,
    }));
}

// ── Play-In Resolution ────────────────────────────────

/** Fetch R1 series that still have a placeholder team name ("Play-In"). */
export async function fetchPlaceholderSeries(): Promise<Array<{ id: string; team_home: string; team_away: string; conference: string }>> {
  const { data, error } = await supabase
    .from('series')
    .select('id, team_home, team_away, conference')
    .eq('round', 'first_round')
    .or('team_home.in.(W7,W8,E7,E8),team_away.in.(W7,W8,E7,E8)');
  if (error) throw error;
  return (data || []).map(s => ({
    id: s.id,
    team_home: s.team_home ?? '',
    team_away: s.team_away ?? '',
    conference: s.conference ?? '',
  }));
}

/** Invoke the resolve-playin Edge Function with the given resolutions. */
export async function resolvePlayin(
  resolutions: Array<{ series_id: string; team_home: string; team_away: string; team_home_logo: string | null; team_away_logo: string | null }>
): Promise<{ ok: boolean; resolved: number; entries_updated: number }> {
  if (!adminSupabase) throw new Error('VITE_SUPABASE_SERVICE_ROLE_KEY not configured');
  const { data, error } = await adminSupabase.functions.invoke('resolve-playin', {
    body: { resolutions },
  });
  if (error) throw error;
  return data as { ok: boolean; resolved: number; entries_updated: number };
}

// ── End-of-tournament feedback ──────────────────────────
/** Fetch the current user's feedback row (null if they haven't submitted yet). */
export async function fetchMyFeedback(userId: string): Promise<FeedbackResponse | null> {
  const { data, error } = await supabase
    .from('feedback_responses')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as FeedbackResponse) ?? null;
}

/** Lightweight existence check (used by the home banner to self-hide). */
export async function hasFeedbackResponse(userId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('feedback_responses')
    .select('user_id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throw error;
  return (count ?? 0) > 0;
}

/** Create or update the current user's feedback (upsert on user_id, bumps updated_at). */
export async function upsertFeedback(userId: string, input: FeedbackInput): Promise<FeedbackResponse> {
  const { data, error } = await supabase
    .from('feedback_responses')
    .upsert(
      { user_id: userId, ...input, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    )
    .select()
    .single();
  if (error) throw error;
  return data as FeedbackResponse;
}

